/**
 * Created by claudio iozzia on 20/04/2017.
 */
import {EMPTY_ENTITY_ALERT, EntityAlert} from "../EntityAlert";
import {IAppSpec} from "../../Redux/selector.state";
import {Ic, IEntityAlert} from "../common";
import {
  IInput, ILedParam, IOutput, ISingleOutput, ISpec
} from "../ISpec";
import {tassign} from "../../common/utils";
import {
  SPEC_NOTE_INPUT_INIT, SPEC_NOTE_LED_INIT,
  SPEC_NOTE_OUTPUTS_INIT
} from "../Spec_States";

export type IValidatorFn<T, U> = (t: T, u: U) => IEntityAlert;
export type IValidatorOutputFn<T, U, V> = (t: T, u: U, v: V) => IEntityAlert;

export type ValidatorFn = IValidatorFn<Ic, IAppSpec>;
export type ValidatorOutputFn = IValidatorOutputFn<Ic, IAppSpec, number>;

export const validatorFn_None: ValidatorFn = (_a: Ic, _spec: IAppSpec): IEntityAlert => {
  return new EntityAlert();
};
export const validatorFn_SingleOutput_None: ValidatorOutputFn = (_a: Ic, _spec: IAppSpec, _o: number): IEntityAlert => {
  return new EntityAlert();
};

export function composeAllValidators<T, U>(validators: IValidatorFn<T, U>[]): IValidatorFn<T, U> {

  return (t, u) => validators.reduce(
    (acc: IEntityAlert, p: IValidatorFn<T, U>) => {

      const note: IEntityAlert = p(t, u);

      return new EntityAlert(
        acc.info.concat(note.info),
        acc.warning.concat(note.warning),
        acc.translatedErrors.concat(note.translatedErrors),
      );
    }

    , EMPTY_ENTITY_ALERT);
}

export function evaluateUntilFirstValidator<T, U>(validators: IValidatorFn<T, U>[]): IValidatorFn<T, U> {

  return (t, u) => validators.reduce(
    (acc: IEntityAlert, p: IValidatorFn<T, U>) => {

      if (acc.hasMessage) {
        return acc;
      } else {
        const note: IEntityAlert = p(t, u);

        return new EntityAlert(
          acc.info.concat(note.info),
          acc.warning.concat(note.warning),
          acc.translatedErrors.concat(note.translatedErrors),
        );
      }
    }

    , EMPTY_ENTITY_ALERT);
}

export function composeAllValidatorsOutput<T, U, V>(validators: IValidatorOutputFn<T, U, V>[]): IValidatorOutputFn<T, U, V> {

  return (t, u, v) => validators.reduce(
    (acc: IEntityAlert, p: IValidatorOutputFn<T, U, V>) => {

      const note: IEntityAlert = p(t, u, v);

      return new EntityAlert(
        acc.info.concat(note.info),
        acc.warning.concat(note.warning),
        acc.translatedErrors.concat(note.translatedErrors),
      );
    }

    , EMPTY_ENTITY_ALERT);
}

export function evaluateUntilFirstValidatorOutput<T, U, V>(validators: IValidatorOutputFn<T, U, V>[]): IValidatorOutputFn<T, U, V> {

  return (t, u, v) => validators.reduce(
    (acc: IEntityAlert, p: IValidatorOutputFn<T, U, V>) => {
      if (acc.hasMessage) {
        return acc;
      } else {
        const note: IEntityAlert = p(t, u, v);
        return new EntityAlert(
          acc.info.concat(note.info),
          acc.warning.concat(note.warning),
          acc.translatedErrors.concat(note.translatedErrors),
        );
      }
    }
    , EMPTY_ENTITY_ALERT);
}


export type ValidatorSpecFn = (t: Ic, u: IAppSpec) => ISpec<IEntityAlert>;
export type ValidatorSpecSelfFn = (t: Ic, u: IAppSpec) => IEntityAlert;
export type ValidatorInputFn = (t: Ic, u: IAppSpec) => IInput<IEntityAlert>;
export type ValidatorFullOutputFn = (t: Ic, u: IAppSpec) => IOutput<IEntityAlert>;
export type ValidatorSingleOutputFn = (t: Ic, u: IAppSpec, o: number) => ISingleOutput<IEntityAlert>;
export type ValidatorGlobalOutputFn = (t: Ic, u: IAppSpec) => ISingleOutput<IEntityAlert>;
export type ValidatorLedParamFn = (t: Ic, u: IAppSpec) => ILedParam<IEntityAlert>;

export interface IValidator {
  validate: ValidatorSpecFn;
}

export type ValidatorCreator = (a: Ic) => IValidator;

export class Validator implements IValidator {

  public static createValidator(_a: Ic): IValidator { return new Validator(); }

  protected get specValidation(): ValidatorSpecSelfFn {return (_ic: Ic, _s: IAppSpec) => EMPTY_ENTITY_ALERT; }
  protected get input(): ValidatorInputFn {return (_ic: Ic, _s: IAppSpec) => SPEC_NOTE_INPUT_INIT; }
  protected get singleOutput(): ValidatorSingleOutputFn {return (_ic: Ic, _s: IAppSpec, _o: number) => SPEC_NOTE_OUTPUTS_INIT; }
  protected get globalOutput(): ValidatorGlobalOutputFn {return (_ic: Ic, _s: IAppSpec) => SPEC_NOTE_OUTPUTS_INIT; }
  protected get ledParam(): ValidatorLedParamFn {return (_ic: Ic, _s: IAppSpec) => SPEC_NOTE_LED_INIT; }
  protected get fullOutput(): ValidatorFullOutputFn {
    return (ic: Ic, s: IAppSpec) => {
      return {
        outputs: s.spec.value.output.outputs.map((x, i) => this.singleOutput(ic, s, i)),
        globalOutput: this.globalOutput(ic, s),
        ledParam: this.ledParam(ic, s)
      };
    };
  }

  validate(ic: Ic, s: IAppSpec): ISpec<IEntityAlert> {
    // some validator report will be used in next validator functions
    // for this reason it is used the _localAppSpec that contains the validation
    // reports updated
    let _localAppSpec: IAppSpec = tassign(s, {
      spec: {
        note: tassign(s.spec.note, {
          input: this.input(ic, s),
          specValidation: this.specValidation(ic, s)
        }),
        value: s.spec.value
      }
    });
    _localAppSpec = tassign(_localAppSpec, {
      spec: tassign(_localAppSpec.spec, {
        note: tassign(_localAppSpec.spec.note, {
          output: this.fullOutput(ic, _localAppSpec)
        })
      })
    });

    return _localAppSpec.spec.note;
  }
}

